import { css, cx } from '@emotion/css';
import { useStudyContext } from '../utils/StudyContext';
import { getDashboardPath } from '../utils/env';

import { LinkText, StyledText } from '@repo/ui/atoms';
import BookingSummary from '../common/BookingSummary';
import CloseButton from '../common/CloseButton';
import PoweredByUxSignals from '../common/PoweredByUxSignals';
import { useTranslationsContext } from '../utils/TranslationsContext';
import { QuestionWrapperStyle } from './stepsStyle';
import type { CalendarStepT } from './types';

type Props = {
    calendarStepAnswer: CalendarStepT;
    header?: string | null;
    subTitle?: string | null;
};

const BookingConfirmed = ({ calendarStepAnswer, header, subTitle }: Props) => {
    const { t } = useTranslationsContext();
    const { study } = useStudyContext();
    const selectedSlot = calendarStepAnswer.slots.find(
        (slot) => slot.startDate === calendarStepAnswer.answer?.startDate,
    );

    return (
        <div
            className={cx(
                QuestionWrapperStyle,
                css`
                    gap: 24px;
                `,
            )}
        >
            <span>
                <StyledText as="h1" variant="h3" align="center">
                    {header || t('confirmationScreen.bookingConfirmed')}
                </StyledText>
                <StyledText variant="body" weight="regular" align="center">
                    {subTitle || t('confirmationScreen.subTitle')}
                </StyledText>
            </span>

            <BookingSummary
                selectedSlot={selectedSlot?.reference}
                ical={calendarStepAnswer.ical}
            />

            <div>
                <StyledText variant="h4" weight="bold" as="h2">
                    {t('confirmationScreen.whatNext')}
                </StyledText>
                <ul className={ListStyle}>
                    <li>
                        <StyledText variant="body" weight="regular">
                            {t('confirmationScreen.willReceiveMail', {
                                mail: calendarStepAnswer.answer?.email
                                    ? `: ${calendarStepAnswer.answer?.email}`
                                    : '',
                            })}
                        </StyledText>
                    </li>
                    <li>
                        <StyledText variant="body" weight="regular">
                            {t('confirmationScreen.reminder')}
                        </StyledText>
                    </li>
                    <li>
                        <StyledText variant="body" weight="regular">
                            {t('confirmationScreen.consent.listContent')}{' '}
                            <LinkText
                                variant="body"
                                href={`${getDashboardPath()}/legal/privacy-policy/${study.legal.id}`}
                            >
                                {t('confirmationScreen.consent.link')}
                            </LinkText>
                        </StyledText>
                    </li>
                </ul>
            </div>
            <div>
                <StyledText variant="body" weight="regular">
                    {t('confirmationScreen.attendance')}{' '}
                </StyledText>
            </div>
            <div className={cx(CenterStyle, FooterStyle)}>
                <CloseButton />
                <PoweredByUxSignals />
            </div>
        </div>
    );
};

export default BookingConfirmed;

const CenterStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 12px;
    > * {
        text-align: center;
    }
`;

const ListStyle = css`
    padding-left: 20px;
    li {
        margin: 10px 0;
    }
`;

const FooterStyle = css`
    margin-top: auto;
    align-self: flex-end;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;
